import React, { useState, useRef } from 'react';
import { Upload, Download, CheckCircle2, AlertTriangle } from 'lucide-react';
import Modal from './Modal';
import api from '../api/client';

/**
 * Bulk Asset CSV Import — button + modal.
 * CSV columns (header row required):
 * asset_number,category,brand,model,serial_number,company,status,purchased_date,purchase_price,vendor,warranty_period,location,remarks
 */
const TEMPLATE_HEADERS = ['asset_number','category','brand','model','serial_number','company','status','purchased_date','purchase_price','vendor','warranty_period','location','remarks'];
const TEMPLATE_SAMPLE = 'CLS-LAP-0001,laptop,Dell,Latitude 5440,SN12345,CLS,available,2026-01-15,290000,Dell Lanka,3_years,Colombo HQ,New stock';

// Minimal CSV parser handling quoted fields
function parseCSV(text) {
  const rows = [];
  let row = [], field = '', inQuotes = false;
  for (let i = 0; i < text.length; i++) {
    const c = text[i];
    if (inQuotes) {
      if (c === '"') { if (text[i + 1] === '"') { field += '"'; i++; } else inQuotes = false; }
      else field += c;
    } else if (c === '"') inQuotes = true;
    else if (c === ',') { row.push(field); field = ''; }
    else if (c === '\n' || c === '\r') {
      if (c === '\r' && text[i + 1] === '\n') i++;
      row.push(field); field = '';
      if (row.some(f => f.trim() !== '')) rows.push(row);
      row = [];
    } else field += c;
  }
  if (field !== '' || row.length) { row.push(field); if (row.some(f => f.trim() !== '')) rows.push(row); }
  return rows;
}

export default function BulkAssetImport({ onImported }) {
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState([]);
  const [fileName, setFileName] = useState('');
  const [parseError, setParseError] = useState('');
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);
  const fileRef = useRef();

  const reset = () => { setRows([]); setFileName(''); setParseError(''); setResult(null); if (fileRef.current) fileRef.current.value = ''; };

  const handleFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    reset();
    setFileName(file.name);
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const parsed = parseCSV(String(reader.result));
        if (parsed.length < 2) { setParseError('CSV must have a header row and at least one data row'); return; }
        const headers = parsed[0].map(h => h.trim().toLowerCase().replace(/\s+/g, '_'));
        if (!headers.includes('asset_number') || !headers.includes('category')) {
          setParseError('Header row must include at least "asset_number" and "category" columns'); return;
        }
        const dataRows = parsed.slice(1).map(cells => {
          const obj = {};
          headers.forEach((h, i) => { obj[h] = (cells[i] || '').trim(); });
          return obj;
        });
        if (dataRows.length > 500) { setParseError(`Too many rows (${dataRows.length}) — maximum 500 per import`); return; }
        setRows(dataRows);
      } catch (err) { setParseError('Could not parse file: ' + err.message); }
    };
    reader.readAsText(file);
  };

  const downloadTemplate = () => {
    const blob = new Blob([TEMPLATE_HEADERS.join(',') + '\n' + TEMPLATE_SAMPLE + '\n'], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = 'asset_import_template.csv';
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const doImport = async () => {
    setImporting(true);
    try {
      const r = await api.post('/assets/bulk-import', { rows });
      setResult(r.data);
      if (r.data.created > 0 && onImported) onImported();
    } catch (err) {
      setResult({ created: 0, errors: [{ line: '-', error: err.response?.data?.error || err.message }] });
    } finally { setImporting(false); }
  };

  return (
    <>
      <button onClick={() => { reset(); setOpen(true); }} className="btn-secondary" title="Import assets from a CSV file">
        <Upload size={13}/> Import CSV
      </button>
      {open && (
        <Modal title="Bulk Asset Import (CSV)" onClose={() => setOpen(false)} size="lg">
          <div className="space-y-4">
            {!result && (<>
              <div className="text-xs text-slate-400 leading-relaxed">
                Upload a CSV with columns: <span className="font-mono text-slate-300">asset_number, category, brand, model, serial_number, company, status, purchased_date (YYYY-MM-DD), purchase_price, vendor, warranty_period, location, remarks</span>.
                Company accepts the company code or name (e.g. <span className="text-slate-300">CLS</span>). Warranty period: 1_year / 2_years / 3_years / 5_years / lifetime.
                Warranty expiry and depreciation are calculated automatically.
              </div>
              <div className="flex items-center gap-3">
                <button onClick={downloadTemplate} className="btn-secondary"><Download size={13}/> Download Template</button>
                <input ref={fileRef} type="file" accept=".csv,text/csv" onChange={handleFile} className="text-xs text-slate-300" />
              </div>
              {parseError && <div className="text-xs text-red-400 flex items-center gap-2"><AlertTriangle size={14}/> {parseError}</div>}
              {rows.length > 0 && (<>
                <div className="text-xs text-emerald-400">Parsed <strong>{rows.length}</strong> row{rows.length > 1 ? 's' : ''} from {fileName} — preview (first 10):</div>
                <div className="overflow-x-auto border border-white/10 rounded-lg" style={{ maxHeight: 260 }}>
                  <table className="w-full text-xs">
                    <thead><tr className="text-slate-500 text-left">
                      {['#','Asset No','Category','Brand','Model','Company','Status','Purchased','Price'].map(h => <th key={h} className="px-2 py-1.5 whitespace-nowrap">{h}</th>)}
                    </tr></thead>
                    <tbody>
                      {rows.slice(0, 10).map((r, i) => (
                        <tr key={i} className="border-t border-white/5 text-slate-300">
                          <td className="px-2 py-1.5 text-slate-500">{i + 2}</td>
                          <td className="px-2 py-1.5 font-mono">{r.asset_number || <span className="text-red-400">missing</span>}</td>
                          <td className="px-2 py-1.5">{r.category || <span className="text-red-400">missing</span>}</td>
                          <td className="px-2 py-1.5">{r.brand}</td><td className="px-2 py-1.5">{r.model}</td>
                          <td className="px-2 py-1.5">{r.company}</td><td className="px-2 py-1.5">{r.status}</td>
                          <td className="px-2 py-1.5">{r.purchased_date}</td><td className="px-2 py-1.5">{r.purchase_price}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <button onClick={doImport} disabled={importing} className="btn-primary w-full justify-center">
                  {importing ? 'Importing…' : `Import ${rows.length} Asset${rows.length > 1 ? 's' : ''}`}
                </button>
              </>)}
            </>)}
            {result && (
              <div className="space-y-3">
                <div className="flex items-center gap-2 text-sm">
                  {result.created > 0
                    ? <><CheckCircle2 size={18} className="text-emerald-400"/><span className="text-emerald-400 font-semibold">{result.created} asset{result.created > 1 ? 's' : ''} imported successfully</span></>
                    : <><AlertTriangle size={18} className="text-amber-400"/><span className="text-amber-400 font-semibold">No assets were imported</span></>}
                </div>
                {result.errors?.length > 0 && (
                  <div className="border border-red-500/30 bg-red-500/5 rounded-lg p-3 space-y-1" style={{ maxHeight: 220, overflowY: 'auto' }}>
                    <div className="text-xs font-semibold text-red-400 mb-1">{result.errors.length} row{result.errors.length > 1 ? 's' : ''} skipped:</div>
                    {result.errors.map((e, i) => (
                      <div key={i} className="text-xs text-red-300">Line {e.line}: {e.error}</div>
                    ))}
                  </div>
                )}
                <div className="flex gap-2">
                  <button onClick={reset} className="btn-secondary flex-1 justify-center">Import Another File</button>
                  <button onClick={() => setOpen(false)} className="btn-primary flex-1 justify-center">Done</button>
                </div>
              </div>
            )}
          </div>
        </Modal>
      )}
    </>
  );
}
